import { z } from 'zod';
import type { ShapeManifest } from '../../../../lib/diagram/shape-manifest.types';
import { renderTagBadgesSvg } from '../../../../lib/diagram/tagBadgeLayout';
import {
  computeBelowLabelBoxHeight,
  escapeSvgAttr,
  formatSvgNumber,
  renderBoxedLabel,
  renderMultilineText,
  resolveFontStyleParts,
  resolveShapePresentation,
} from '../../../diagram/shapes/svgRenderer.utils';
import { BPMN_SHAPES, BPMN_SHAPE_IDS, type BpmnShapeId } from './BpmnShapes.defs';

export type BpmnEventType = 'start' | 'intermediate' | 'end' | 'boundary';
export type BpmnEventMode = 'catching' | 'throwing';
export type BpmnTriggerType =
  | 'none'
  | 'message'
  | 'timer'
  | 'error'
  | 'signal'
  | 'conditional'
  | 'link'
  | 'terminate'
  | 'escalation'
  | 'cancel'
  | 'compensation'
  | 'multiple'
  | 'parallel-multiple';
export type BpmnGatewayType =
  | 'none'
  | 'exclusive'
  | 'parallel'
  | 'parallel-event'
  | 'inclusive'
  | 'event-based'
  | 'exclusive-event'
  | 'complex';
export type BpmnActivityType = 'task' | 'sub-process' | 'event-sub-process' | 'call-activity' | 'transaction';
export type BpmnTaskType = 'none' | 'user' | 'service' | 'script' | 'manual' | 'business-rule' | 'send' | 'receive';
export type BpmnLoopType = 'none' | 'standard' | 'multi-instance-parallel' | 'multi-instance-sequential';
export type BpmnActivityMarker =
  | 'none'
  | 'sub-process'
  | 'loop'
  | 'multi-instance-parallel'
  | 'multi-instance-sequential'
  | 'ad-hoc'
  | 'compensation'
  | 'reset';
export type BpmnDataType = 'data-object' | 'data-store' | 'message';

const BPMN_DEFAULTS = {
  fill: '#FFFFFF',
  stroke: '#111827',
  strokeWidth: 1.6,
  textColor: '#111827',
  fontSize: 12,
};

const BPMN_LABEL_MARGIN_Y = 4;

const bpmnShapeProps = z.object({
  label: z.string().optional(),
  eventType: z.enum(['start', 'intermediate', 'end', 'boundary']).optional().default('start'),
  eventMode: z.enum(['catching', 'throwing']).optional().default('catching'),
  trigger: z.enum(['none', 'message', 'timer', 'error', 'signal', 'conditional', 'link', 'terminate', 'escalation', 'cancel', 'compensation', 'multiple', 'parallel-multiple']).optional().default('none'),
  interrupting: z.boolean().optional().default(true),
  gatewayType: z.enum(['none', 'exclusive', 'parallel', 'parallel-event', 'inclusive', 'event-based', 'exclusive-event', 'complex']).optional().default('none'),
  activityType: z.enum(['task', 'sub-process', 'event-sub-process', 'call-activity', 'transaction']).optional().default('task'),
  taskType: z.enum(['none', 'user', 'service', 'script', 'manual', 'business-rule', 'send', 'receive']).optional().default('none'),
  loopType: z.enum(['none', 'standard', 'multi-instance-parallel', 'multi-instance-sequential']).optional().default('none'),
  activityMarker: z.enum(['none', 'sub-process', 'loop', 'multi-instance-parallel', 'multi-instance-sequential', 'ad-hoc', 'compensation', 'reset']).optional().default('none'),
  dataType: z.enum(['data-object', 'data-store', 'message']).optional().default('data-store'),
  fillColor: z.string().optional(),
  strokeColor: z.string().optional(),
  strokeWidth: z.number().optional(),
  attrs: z.record(z.string(), z.any()).optional(),
});

export type BpmnShapeProps = z.infer<typeof bpmnShapeProps>;

const resolveBpmnStrokeWidth = (id: BpmnShapeId, props: BpmnShapeProps): number => {
  if (typeof props.strokeWidth === 'number') return props.strokeWidth;
  if (id !== 'bpmn-event') return BPMN_DEFAULTS.strokeWidth;
  const eventType = props.eventType ?? 'start';
  const eventMode = props.eventMode ?? (eventType === 'end' ? 'throwing' : 'catching');
  if (eventType === 'end') return 3.2;
  if (eventMode === 'throwing') return 2.4;
  return BPMN_DEFAULTS.strokeWidth;
};

const renderEventTriggerSvg = (
  triggerType: BpmnTriggerType,
  width: number,
  height: number,
  stroke: string,
  sw: string,
): string => {
  const cx = width / 2;
  const cy = height / 2;
  const iconSize = Math.max(6, Math.min(width, height) * 0.23);
  const left = cx - iconSize;
  const right = cx + iconSize;
  const top = cy - iconSize * 0.72;
  const bottom = cy + iconSize * 0.72;

  switch (triggerType) {
    case 'message':
      return [
        `<rect x="${formatSvgNumber(left)}" y="${formatSvgNumber(top)}" width="${formatSvgNumber(iconSize * 2)}" height="${formatSvgNumber(iconSize * 1.45)}" rx="${formatSvgNumber(iconSize * 0.12)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<path d="M ${formatSvgNumber(left)} ${formatSvgNumber(top)} L ${formatSvgNumber(cx)} ${formatSvgNumber(cy + iconSize * 0.12)} L ${formatSvgNumber(right)} ${formatSvgNumber(top)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round" />`,
      ].join('');
    case 'timer': {
      const r = iconSize * 1.02;
      return [
        `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(r)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<line x1="${formatSvgNumber(cx)}" y1="${formatSvgNumber(cy)}" x2="${formatSvgNumber(cx)}" y2="${formatSvgNumber(cy - r * 0.46)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(cx)}" y1="${formatSvgNumber(cy)}" x2="${formatSvgNumber(cx + r * 0.36)}" y2="${formatSvgNumber(cy)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
      ].join('');
    }
    case 'escalation':
      return `<polygon points="${formatSvgNumber(cx)} ${formatSvgNumber(cy - iconSize * 0.9)} ${formatSvgNumber(cx + iconSize * 0.78)} ${formatSvgNumber(cy + iconSize * 0.82)} ${formatSvgNumber(cx)} ${formatSvgNumber(cy + iconSize * 0.24)} ${formatSvgNumber(cx - iconSize * 0.78)} ${formatSvgNumber(cy + iconSize * 0.82)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="miter" />`;
    case 'conditional':
      return [
        `<rect x="${formatSvgNumber(left + iconSize * 0.1)}" y="${formatSvgNumber(top - iconSize * 0.1)}" width="${formatSvgNumber(iconSize * 1.8)}" height="${formatSvgNumber(iconSize * 1.8)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<line x1="${formatSvgNumber(left + iconSize * 0.36)}" y1="${formatSvgNumber(top + iconSize * 0.42)}" x2="${formatSvgNumber(right - iconSize * 0.36)}" y2="${formatSvgNumber(top + iconSize * 0.42)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(left + iconSize * 0.36)}" y1="${formatSvgNumber(cy)}" x2="${formatSvgNumber(right - iconSize * 0.36)}" y2="${formatSvgNumber(cy)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(left + iconSize * 0.36)}" y1="${formatSvgNumber(bottom - iconSize * 0.42)}" x2="${formatSvgNumber(right - iconSize * 0.36)}" y2="${formatSvgNumber(bottom - iconSize * 0.42)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
      ].join('');
    case 'link':
      return `<path d="M ${formatSvgNumber(left)} ${formatSvgNumber(cy - iconSize * 0.24)} L ${formatSvgNumber(right - iconSize * 0.52)} ${formatSvgNumber(cy - iconSize * 0.24)} L ${formatSvgNumber(right - iconSize * 0.52)} ${formatSvgNumber(cy - iconSize * 0.72)} L ${formatSvgNumber(right)} ${formatSvgNumber(cy)} L ${formatSvgNumber(right - iconSize * 0.52)} ${formatSvgNumber(cy + iconSize * 0.72)} L ${formatSvgNumber(right - iconSize * 0.52)} ${formatSvgNumber(cy + iconSize * 0.24)} L ${formatSvgNumber(left)} ${formatSvgNumber(cy + iconSize * 0.24)} Z" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round" />`;
    case 'error':
      {
        const zapSize = iconSize * 1.12;
        return `<path d="M ${formatSvgNumber(cx + zapSize * 0.18)} ${formatSvgNumber(cy - zapSize * 0.82)} L ${formatSvgNumber(cx - zapSize * 0.62)} ${formatSvgNumber(cy + zapSize * 0.04)} L ${formatSvgNumber(cx - zapSize * 0.10)} ${formatSvgNumber(cy + zapSize * 0.04)} L ${formatSvgNumber(cx - zapSize * 0.22)} ${formatSvgNumber(cy + zapSize * 0.82)} L ${formatSvgNumber(cx + zapSize * 0.62)} ${formatSvgNumber(cy - zapSize * 0.04)} L ${formatSvgNumber(cx + zapSize * 0.10)} ${formatSvgNumber(cy - zapSize * 0.04)} L ${formatSvgNumber(cx + zapSize * 0.18)} ${formatSvgNumber(cy - zapSize * 0.82)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round" />`;
      }
    case 'cancel':
      {
        const scale = (iconSize * 0.82) / 4.879;
        const sx = (value: number) => cx + (value - 12) * scale;
        const sy = (value: number) => cy + (value - 12) * scale;
        return `<path d="M ${formatSvgNumber(sx(14.758))} ${formatSvgNumber(sy(7.121))} L ${formatSvgNumber(sx(16.879))} ${formatSvgNumber(sy(9.242))} L ${formatSvgNumber(sx(14.121))} ${formatSvgNumber(sy(12))} L ${formatSvgNumber(sx(16.879))} ${formatSvgNumber(sy(14.758))} L ${formatSvgNumber(sx(14.758))} ${formatSvgNumber(sy(16.879))} L ${formatSvgNumber(sx(12))} ${formatSvgNumber(sy(14.121))} L ${formatSvgNumber(sx(9.242))} ${formatSvgNumber(sy(16.879))} L ${formatSvgNumber(sx(7.121))} ${formatSvgNumber(sy(14.758))} L ${formatSvgNumber(sx(9.879))} ${formatSvgNumber(sy(12))} L ${formatSvgNumber(sx(7.121))} ${formatSvgNumber(sy(9.242))} L ${formatSvgNumber(sx(9.242))} ${formatSvgNumber(sy(7.121))} L ${formatSvgNumber(sx(12))} ${formatSvgNumber(sy(9.879))} Z" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round" />`;
      }
    case 'compensation':
      return [
        `<polygon points="${formatSvgNumber(cx - iconSize * 0.08)} ${formatSvgNumber(cy - iconSize * 0.62)} ${formatSvgNumber(cx - iconSize * 0.72)} ${formatSvgNumber(cy)} ${formatSvgNumber(cx - iconSize * 0.08)} ${formatSvgNumber(cy + iconSize * 0.62)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" />`,
        `<polygon points="${formatSvgNumber(cx + iconSize * 0.66)} ${formatSvgNumber(cy - iconSize * 0.62)} ${formatSvgNumber(cx + iconSize * 0.02)} ${formatSvgNumber(cy)} ${formatSvgNumber(cx + iconSize * 0.66)} ${formatSvgNumber(cy + iconSize * 0.62)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" />`,
      ].join('');
    case 'signal':
      return `<polygon points="${formatSvgNumber(cx)} ${formatSvgNumber(top)} ${formatSvgNumber(right)} ${formatSvgNumber(bottom)} ${formatSvgNumber(left)} ${formatSvgNumber(bottom)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" />`;
    case 'multiple':
      return `<polygon points="${formatSvgNumber(cx)} ${formatSvgNumber(top)} ${formatSvgNumber(cx + iconSize * 0.88)} ${formatSvgNumber(cy - iconSize * 0.26)} ${formatSvgNumber(cx + iconSize * 0.54)} ${formatSvgNumber(bottom)} ${formatSvgNumber(cx - iconSize * 0.54)} ${formatSvgNumber(bottom)} ${formatSvgNumber(cx - iconSize * 0.88)} ${formatSvgNumber(cy - iconSize * 0.26)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" />`;
    case 'parallel-multiple':
      return [
        `<line x1="${formatSvgNumber(cx - iconSize * 0.82)}" y1="${formatSvgNumber(cy)}" x2="${formatSvgNumber(cx + iconSize * 0.82)}" y2="${formatSvgNumber(cy)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(cx)}" y1="${formatSvgNumber(cy - iconSize * 0.82)}" x2="${formatSvgNumber(cx)}" y2="${formatSvgNumber(cy + iconSize * 0.82)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
      ].join('');
    case 'terminate':
      return `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(iconSize * 0.72)}" fill="${stroke}" stroke="${stroke}" stroke-width="${sw}" />`;
    case 'none':
    default:
      return '';
  }
};

const renderEventMarkerSvg = (
  eventType: BpmnEventType,
  interrupting: boolean,
  eventMode: BpmnEventMode,
  triggerType: BpmnTriggerType,
  width: number,
  height: number,
  stroke: string,
): string => {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.max(10, Math.min(width, height) * 0.48);
  const isDashed = !interrupting;
  const isDouble = eventType === 'intermediate';
  const isEnd = eventType === 'end';
  const isThrowing = eventMode === 'throwing';
  const outerStrokeWidth = isEnd ? 3.2 : isThrowing ? 2.4 : 1.8;
  const innerStrokeWidth = isThrowing ? 2.1 : 1.5;
  const dashArray = isDashed ? '4 3' : '';
  const outerSw = formatSvgNumber(outerStrokeWidth);
  const innerSw = formatSvgNumber(innerStrokeWidth);
  const triggerStrokeWidth = formatSvgNumber(Math.max(1.3, (isThrowing ? 1.8 : 1.5)));
  const circles = [
    `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(radius)}" fill="#FFFFFF" stroke="${stroke}" stroke-width="${outerSw}"${dashArray ? ` stroke-dasharray="${dashArray}"` : ''} />`,
  ];
  if (isDouble) {
    circles.push(
      `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(radius - 4.8)}" fill="none" stroke="${stroke}" stroke-width="${innerSw}"${dashArray ? ` stroke-dasharray="${dashArray}"` : ''} />`
    );
  }
  const triggerMarkup = renderEventTriggerSvg(triggerType, width, height, isEnd ? '#FFFFFF' : stroke, triggerStrokeWidth);
  return circles.join('') + triggerMarkup;
};

const renderGatewayMarkerSvg = (
  gatewayType: BpmnGatewayType,
  width: number,
  height: number,
  stroke: string,
  sw: string,
): string => {
  const cx = width / 2;
  const cy = height / 2;
  const size = Math.max(8, Math.min(width, height) * 0.18);

  switch (gatewayType) {
    case 'exclusive':
      return [
        `<line x1="${formatSvgNumber(cx - size)}" y1="${formatSvgNumber(cy - size)}" x2="${formatSvgNumber(cx + size)}" y2="${formatSvgNumber(cy + size)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(cx + size)}" y1="${formatSvgNumber(cy - size)}" x2="${formatSvgNumber(cx - size)}" y2="${formatSvgNumber(cy + size)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
      ].join('');
    case 'parallel':
      return [
        `<line x1="${formatSvgNumber(cx - size)}" y1="${formatSvgNumber(cy)}" x2="${formatSvgNumber(cx + size)}" y2="${formatSvgNumber(cy)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(cx)}" y1="${formatSvgNumber(cy - size)}" x2="${formatSvgNumber(cx)}" y2="${formatSvgNumber(cy + size)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
      ].join('');
    case 'inclusive':
      return `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(size)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`;
    case 'event-based': {
      const innerCircle = size * 0.72;
      const pentagonSize = size * 0.46;
      return [
        `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(size)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(innerCircle)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<polygon points="${formatSvgNumber(cx)} ${formatSvgNumber(cy - pentagonSize)} ${formatSvgNumber(cx + pentagonSize * 0.95)} ${formatSvgNumber(cy - pentagonSize * 0.3)} ${formatSvgNumber(cx + pentagonSize * 0.58)} ${formatSvgNumber(cy + pentagonSize * 0.8)} ${formatSvgNumber(cx - pentagonSize * 0.58)} ${formatSvgNumber(cy + pentagonSize * 0.8)} ${formatSvgNumber(cx - pentagonSize * 0.95)} ${formatSvgNumber(cy - pentagonSize * 0.3)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" />`,
      ].join('');
    }
    case 'exclusive-event':
      return [
        `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(size)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<polygon points="${formatSvgNumber(cx)} ${formatSvgNumber(cy - size * 0.62)} ${formatSvgNumber(cx + size * 0.59)} ${formatSvgNumber(cy - size * 0.2)} ${formatSvgNumber(cx + size * 0.36)} ${formatSvgNumber(cy + size * 0.5)} ${formatSvgNumber(cx - size * 0.36)} ${formatSvgNumber(cy + size * 0.5)} ${formatSvgNumber(cx - size * 0.59)} ${formatSvgNumber(cy - size * 0.2)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" />`,
      ].join('');
    case 'complex':
      return [
        `<line x1="${formatSvgNumber(cx - size)}" y1="${formatSvgNumber(cy)}" x2="${formatSvgNumber(cx + size)}" y2="${formatSvgNumber(cy)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(cx)}" y1="${formatSvgNumber(cy - size)}" x2="${formatSvgNumber(cx)}" y2="${formatSvgNumber(cy + size)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(cx - size * 0.72)}" y1="${formatSvgNumber(cy - size * 0.72)}" x2="${formatSvgNumber(cx + size * 0.72)}" y2="${formatSvgNumber(cy + size * 0.72)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<line x1="${formatSvgNumber(cx + size * 0.72)}" y1="${formatSvgNumber(cy - size * 0.72)}" x2="${formatSvgNumber(cx - size * 0.72)}" y2="${formatSvgNumber(cy + size * 0.72)}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
      ].join('');
    case 'parallel-event':
      {
        const plusArm = size * 0.62;
        const plusHalfThickness = size * 0.22;
        const plusPath = [
          `M ${formatSvgNumber(cx - plusHalfThickness)} ${formatSvgNumber(cy - plusArm)}`,
          `L ${formatSvgNumber(cx + plusHalfThickness)} ${formatSvgNumber(cy - plusArm)}`,
          `L ${formatSvgNumber(cx + plusHalfThickness)} ${formatSvgNumber(cy - plusHalfThickness)}`,
          `L ${formatSvgNumber(cx + plusArm)} ${formatSvgNumber(cy - plusHalfThickness)}`,
          `L ${formatSvgNumber(cx + plusArm)} ${formatSvgNumber(cy + plusHalfThickness)}`,
          `L ${formatSvgNumber(cx + plusHalfThickness)} ${formatSvgNumber(cy + plusHalfThickness)}`,
          `L ${formatSvgNumber(cx + plusHalfThickness)} ${formatSvgNumber(cy + plusArm)}`,
          `L ${formatSvgNumber(cx - plusHalfThickness)} ${formatSvgNumber(cy + plusArm)}`,
          `L ${formatSvgNumber(cx - plusHalfThickness)} ${formatSvgNumber(cy + plusHalfThickness)}`,
          `L ${formatSvgNumber(cx - plusArm)} ${formatSvgNumber(cy + plusHalfThickness)}`,
          `L ${formatSvgNumber(cx - plusArm)} ${formatSvgNumber(cy - plusHalfThickness)}`,
          `L ${formatSvgNumber(cx - plusHalfThickness)} ${formatSvgNumber(cy - plusHalfThickness)}`,
          'Z',
        ].join(' ');
        return [
          `<circle cx="${formatSvgNumber(cx)}" cy="${formatSvgNumber(cy)}" r="${formatSvgNumber(size)}" fill="#FFFFFF" stroke="#000000" stroke-width="${sw}" />`,
          `<path d="${plusPath}" fill="#FFFFFF" stroke="#000000" stroke-width="${sw}" stroke-linejoin="round" />`,
        ].join('');
      }
    case 'none':
    default:
      return '';
  }
};

const renderActivityStyleSvg = (
  activityType: BpmnActivityType,
  width: number,
  height: number,
  stroke: string,
  sw: string,
): string => {
  switch (activityType) {
    case 'transaction':
      return [
        `<rect x="${formatSvgNumber(width * 0.012)}" y="${formatSvgNumber(height * 0.012)}" width="${formatSvgNumber(width * 0.976)}" height="${formatSvgNumber(height * 0.976)}" rx="${formatSvgNumber(Math.min(width, height) * 0.06)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<rect x="${formatSvgNumber(width * 0.06)}" y="${formatSvgNumber(height * 0.06)}" width="${formatSvgNumber(width * 0.88)}" height="${formatSvgNumber(height * 0.88)}" rx="${formatSvgNumber(Math.min(width, height) * 0.06)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
      ].join('');
    case 'event-sub-process':
      return `<rect x="${formatSvgNumber(width * 0.012)}" y="${formatSvgNumber(height * 0.012)}" width="${formatSvgNumber(width * 0.976)}" height="${formatSvgNumber(height * 0.976)}" rx="${formatSvgNumber(Math.min(width, height) * 0.06)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-dasharray="4 3" />`;
    case 'call-activity':
      return `<rect x="${formatSvgNumber(width * 0.012)}" y="${formatSvgNumber(height * 0.012)}" width="${formatSvgNumber(width * 0.976)}" height="${formatSvgNumber(height * 0.976)}" rx="${formatSvgNumber(Math.min(width, height) * 0.06)}" fill="none" stroke="${stroke}" stroke-width="${formatSvgNumber(Math.max(Number(sw) + 0.7, 2.2))}" />`;
    case 'sub-process':
    case 'task':
    default:
      return `<rect x="${formatSvgNumber(width * 0.012)}" y="${formatSvgNumber(height * 0.012)}" width="${formatSvgNumber(width * 0.976)}" height="${formatSvgNumber(height * 0.976)}" rx="${formatSvgNumber(Math.min(width, height) * 0.06)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`;
  }
};

const renderActivityMarkerSvg = (
  markerType: BpmnActivityMarker,
  width: number,
  height: number,
  stroke: string,
  sw: string,
): string => {
  const cx = width / 2;
  const iconSize = Math.max(6, Math.min(width, height) * 0.095);
  const bottomPadding = Math.max(6, iconSize * 0.35);
  const y = height - iconSize - bottomPadding;
  switch (markerType) {
    case 'sub-process':
      return [
        `<rect x="${formatSvgNumber(cx - iconSize)}" y="${formatSvgNumber(y - iconSize)}" width="${formatSvgNumber(iconSize * 2)}" height="${formatSvgNumber(iconSize * 2)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<line x1="${formatSvgNumber(cx - iconSize * 0.5)}" y1="${formatSvgNumber(y)}" x2="${formatSvgNumber(cx + iconSize * 0.5)}" y2="${formatSvgNumber(y)}" stroke="${stroke}" stroke-width="${sw}" />`,
        `<line x1="${formatSvgNumber(cx)}" y1="${formatSvgNumber(y - iconSize * 0.5)}" x2="${formatSvgNumber(cx)}" y2="${formatSvgNumber(y + iconSize * 0.5)}" stroke="${stroke}" stroke-width="${sw}" />`,
      ].join('');
    case 'loop':
    case 'reset':
      return [
        `<path d="M ${formatSvgNumber(cx + iconSize * 0.52)} ${formatSvgNumber(y + iconSize * 0.06)} A ${formatSvgNumber(iconSize * 0.62)} ${formatSvgNumber(iconSize * 0.62)} 0 1 1 ${formatSvgNumber(cx - iconSize * 0.53)} ${formatSvgNumber(y - iconSize * 0.09)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`,
        `<polyline points="${formatSvgNumber(cx - iconSize * 0.6)} ${formatSvgNumber(y - iconSize * 0.3)} ${formatSvgNumber(cx - iconSize * 0.53)} ${formatSvgNumber(y - iconSize * 0.09)} ${formatSvgNumber(cx - iconSize * 0.32)} ${formatSvgNumber(y - iconSize * 0.14)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round" />`,
      ].join('');
    case 'multi-instance-parallel':
      return [
        `<line x1="${formatSvgNumber(cx - iconSize * 0.55)}" y1="${formatSvgNumber(y - iconSize * 0.8)}" x2="${formatSvgNumber(cx - iconSize * 0.55)}" y2="${formatSvgNumber(y + iconSize * 0.8)}" stroke="${stroke}" stroke-width="${sw}" />`,
        `<line x1="${formatSvgNumber(cx)}" y1="${formatSvgNumber(y - iconSize * 0.8)}" x2="${formatSvgNumber(cx)}" y2="${formatSvgNumber(y + iconSize * 0.8)}" stroke="${stroke}" stroke-width="${sw}" />`,
        `<line x1="${formatSvgNumber(cx + iconSize * 0.55)}" y1="${formatSvgNumber(y - iconSize * 0.8)}" x2="${formatSvgNumber(cx + iconSize * 0.55)}" y2="${formatSvgNumber(y + iconSize * 0.8)}" stroke="${stroke}" stroke-width="${sw}" />`,
      ].join('');
    case 'multi-instance-sequential':
      return [
        `<line x1="${formatSvgNumber(cx - iconSize * 0.8)}" y1="${formatSvgNumber(y - iconSize * 0.5)}" x2="${formatSvgNumber(cx + iconSize * 0.8)}" y2="${formatSvgNumber(y - iconSize * 0.5)}" stroke="${stroke}" stroke-width="${sw}" />`,
        `<line x1="${formatSvgNumber(cx - iconSize * 0.8)}" y1="${formatSvgNumber(y)}" x2="${formatSvgNumber(cx + iconSize * 0.8)}" y2="${formatSvgNumber(y)}" stroke="${stroke}" stroke-width="${sw}" />`,
        `<line x1="${formatSvgNumber(cx - iconSize * 0.8)}" y1="${formatSvgNumber(y + iconSize * 0.5)}" x2="${formatSvgNumber(cx + iconSize * 0.8)}" y2="${formatSvgNumber(y + iconSize * 0.5)}" stroke="${stroke}" stroke-width="${sw}" />`,
      ].join('');
    case 'ad-hoc':
      return `<path d="M ${formatSvgNumber(cx - iconSize * 0.85)} ${formatSvgNumber(y + iconSize * 0.2)} C ${formatSvgNumber(cx - iconSize * 0.45)} ${formatSvgNumber(y - iconSize * 0.65)} ${formatSvgNumber(cx + iconSize * 0.1)} ${formatSvgNumber(y + iconSize * 0.65)} ${formatSvgNumber(cx + iconSize * 0.8)} ${formatSvgNumber(y - iconSize * 0.1)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" />`;
    case 'compensation':
      return [
        `<polygon points="${formatSvgNumber(cx - iconSize * 0.08)} ${formatSvgNumber(y - iconSize * 0.62)} ${formatSvgNumber(cx - iconSize * 0.72)} ${formatSvgNumber(y)} ${formatSvgNumber(cx - iconSize * 0.08)} ${formatSvgNumber(y + iconSize * 0.62)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" />`,
        `<polygon points="${formatSvgNumber(cx + iconSize * 0.66)} ${formatSvgNumber(y - iconSize * 0.62)} ${formatSvgNumber(cx + iconSize * 0.02)} ${formatSvgNumber(y)} ${formatSvgNumber(cx + iconSize * 0.66)} ${formatSvgNumber(y + iconSize * 0.62)}" fill="none" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" />`,
      ].join('');
    case 'none':
    default:
      return '';
  }
};

const renderActivityTaskSvg = (
  taskType: BpmnTaskType,
  width: number,
  height: number,
  stroke: string,
  sw: string,
): string => {
  const iconSize = Math.max(8, Math.min(width, height) * 0.13);
  const x = Math.max(8, width * 0.06);
  const y = Math.max(8, height * 0.08);
  const cx = x + iconSize * 0.5;
  switch (taskType) {
    case 'send':
      return [
        `<rect x="${formatSvgNumber(x)}" y="${formatSvgNumber(y)}" width="${formatSvgNumber(iconSize)}" height="${formatSvgNumber(iconSize * 0.72)}" fill="${stroke}" stroke="${stroke}" stroke-width="${sw}" />`,
        `<path d="M ${formatSvgNumber(x)} ${formatSvgNumber(y)} L ${formatSvgNumber(cx)} ${formatSvgNumber(y + iconSize * 0.36)} L ${formatSvgNumber(x + iconSize)} ${formatSvgNumber(y)}" fill="none" stroke="#FFFFFF" stroke-width="${sw}" />`,
      ].join('');
    case 'receive':
      return [
        `<rect x="${formatSvgNumber(x)}" y="${formatSvgNumber(y)}" width="${formatSvgNumber(iconSize)}" height="${formatSvgNumber(iconSize * 0.72)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
        `<path d="M ${formatSvgNumber(x)} ${formatSvgNumber(y)} L ${formatSvgNumber(cx)} ${formatSvgNumber(y + iconSize * 0.36)} L ${formatSvgNumber(x + iconSize)} ${formatSvgNumber(y)}" fill="none" stroke="${stroke}" stroke-width="${sw}" />`,
      ].join('');
    case 'user':
      {
        const s = iconSize / 24;
        const t = `translate(${formatSvgNumber(x)} ${formatSvgNumber(y)}) scale(${formatSvgNumber(s)})`;
        const userSw = formatSvgNumber(Number(sw) * 0.95);
        return [
          `<circle cx="12" cy="8" r="5" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${userSw}" stroke-linecap="round" stroke-linejoin="round" />`,
          `<path d="M20 21a8 8 0 0 0-16 0" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${userSw}" stroke-linecap="round" stroke-linejoin="round" />`,
        ].join('');
      }
    case 'manual':
      {
        const s = iconSize / 24;
        const t = `translate(${formatSvgNumber(x)} ${formatSvgNumber(y)}) scale(${formatSvgNumber(s)})`;
        const manualSw = formatSvgNumber(Number(sw) * 0.72);
        return [
          `<path d="M5.4 13.8C5.4 12.9 6.1 12.2 7 12.2H8.7V9.7L10.3 8L12.1 9V10.6H17.7C18.5 10.6 19.1 11.2 19.1 12C19.1 12.8 18.5 13.4 17.7 13.4H17.9C18.7 13.4 19.3 14 19.3 14.8C19.3 15.6 18.7 16.2 17.9 16.2H17.3C17.9 16.2 18.3 16.8 18.3 17.4C18.3 18.2 17.7 18.8 16.9 18.8H10.2C9.2 18.8 8.3 18.4 7.6 17.7L5.9 16C5.58 15.68 5.4 15.25 5.4 14.8V13.8Z" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${manualSw}" stroke-linecap="round" stroke-linejoin="round" />`,
          `<path d="M11.6 13.2H17.1" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${manualSw}" stroke-linecap="round" stroke-linejoin="round" />`,
          `<path d="M11.6 15.0H17.3" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${manualSw}" stroke-linecap="round" stroke-linejoin="round" />`,
          `<path d="M11.6 16.8H16.5" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${manualSw}" stroke-linecap="round" stroke-linejoin="round" />`,
        ].join('');
      }
    case 'business-rule':
      {
        const businessSw = formatSvgNumber(Number(sw) * 0.72);
      return [
        `<rect x="${formatSvgNumber(x)}" y="${formatSvgNumber(y)}" width="${formatSvgNumber(iconSize)}" height="${formatSvgNumber(iconSize * 0.72)}" fill="none" stroke="${stroke}" stroke-width="${businessSw}" />`,
        `<line x1="${formatSvgNumber(x)}" y1="${formatSvgNumber(y + iconSize * 0.26)}" x2="${formatSvgNumber(x + iconSize)}" y2="${formatSvgNumber(y + iconSize * 0.26)}" stroke="${stroke}" stroke-width="${businessSw}" />`,
        `<line x1="${formatSvgNumber(x + iconSize * 0.34)}" y1="${formatSvgNumber(y + iconSize * 0.26)}" x2="${formatSvgNumber(x + iconSize * 0.34)}" y2="${formatSvgNumber(y + iconSize * 0.72)}" stroke="${stroke}" stroke-width="${businessSw}" />`,
      ].join('');
      }
    case 'service':
      {
        const s = iconSize / 24;
        const t = `translate(${formatSvgNumber(x)} ${formatSvgNumber(y)}) scale(${formatSvgNumber(s)})`;
        const mainSw = formatSvgNumber(Number(sw) * 0.82);
        const minorSw = formatSvgNumber(Number(sw) * 0.72);
        return [
          `<circle cx="10.2" cy="13.1" r="2.6" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${mainSw}" />`,
          `<path d="M10.2 9V10.1M10.2 16.1V17.2M6.1 13.1H7.2M13.2 13.1H14.3M7.3 10.3L8.1 11.1M12.3 15.3L13.1 16.1M7.3 15.9L8.1 15.1M12.3 10.9L13.1 10.1" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${mainSw}" stroke-linecap="round" stroke-linejoin="round" />`,
          `<circle cx="14.2" cy="9.2" r="2" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${minorSw}" />`,
          `<path d="M14.2 6.3V7.1M14.2 11.3V12.1M11.3 9.2H12.1M16.3 9.2H17.1M12.3 7.3L12.9 7.9M15.5 10.5L16.1 11.1M12.3 11.1L12.9 10.5M15.5 7.9L16.1 7.3" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${minorSw}" stroke-linecap="round" stroke-linejoin="round" />`,
        ].join('');
      }
    case 'script':
      {
        const s = iconSize / 24;
        const t = `translate(${formatSvgNumber(x)} ${formatSvgNumber(y)}) scale(${formatSvgNumber(s)})`;
        const scriptSw = formatSvgNumber(Number(sw) * 0.95);
        return [
          `<path d="M15 12h-5" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${scriptSw}" stroke-linecap="round" stroke-linejoin="round" />`,
          `<path d="M15 8h-5" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${scriptSw}" stroke-linecap="round" stroke-linejoin="round" />`,
          `<path d="M19 17V5a2 2 0 0 0-2-2H4" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${scriptSw}" stroke-linecap="round" stroke-linejoin="round" />`,
          `<path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" transform="${t}" fill="none" stroke="${stroke}" stroke-width="${scriptSw}" stroke-linecap="round" stroke-linejoin="round" />`,
        ].join('');
      }
    case 'none':
    default:
      return '';
  }
};

const renderBpmnShapeSVG = (
  id: BpmnShapeId,
  dimensions: { width: number; height: number },
  args: {
    viewModel: { width?: number; height?: number; style?: Record<string, unknown> };
    props: BpmnShapeProps & { attrs?: Record<string, unknown> };
  },
): string => {
  const def = BPMN_SHAPES[id];
  const width = typeof args.viewModel?.width === 'number' && Number.isFinite(args.viewModel.width) && args.viewModel.width > 0
    ? args.viewModel.width
    : dimensions.width;
  const height = typeof args.viewModel?.height === 'number' && Number.isFinite(args.viewModel.height) && args.viewModel.height > 0
    ? args.viewModel.height
    : dimensions.height;
  const presentation = resolveShapePresentation(args.props, args.viewModel, {
    ...BPMN_DEFAULTS,
    strokeWidth: resolveBpmnStrokeWidth(id, args.props),
  });

  const scaleX = def.width > 0 ? width / def.width : 1;
  const scaleY = def.height > 0 ? height / def.height : 1;
  const isDataStore = id === 'bpmn-data-store';
  const transformAttr = isDataStore
    ? ` transform="translate(${formatSvgNumber(-12.5 * scaleX)} ${formatSvgNumber(-6.6667 * scaleY)}) scale(${formatSvgNumber(scaleX)} ${formatSvgNumber(scaleY)})"`
    : scaleX === 1 && scaleY === 1
      ? ''
      : ` transform="scale(${formatSvgNumber(scaleX)} ${formatSvgNumber(scaleY)})"`;
  const activityType = (args.props.activityType ?? 'task') as BpmnActivityType;
  const activityBaseStrokeWidth =
    id === 'bpmn-activity' && activityType === 'call-activity'
      ? Math.max(presentation.strokeWidth + 1.2, 2.6)
      : presentation.strokeWidth;
  const activityBaseDash =
    id === 'bpmn-activity' && activityType === 'event-sub-process' ? '6 4' : '';
  const baseFill = id === 'bpmn-annotation' ? 'none' : presentation.fill;
  const hasFillPaths = Boolean(def.fillPaths && def.fillPaths.length > 0);
  const fillOnlyAttrs =
    `fill="${escapeSvgAttr(baseFill)}" stroke="none" vector-effect="non-scaling-stroke" fill-rule="evenodd" clip-rule="evenodd"`;
  const strokeOnlyAttrs =
    `fill="none" stroke="${escapeSvgAttr(presentation.stroke)}" stroke-width="${formatSvgNumber(activityBaseStrokeWidth)}"${activityBaseDash ? ` stroke-dasharray="${activityBaseDash}"` : ''} stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke" fill-rule="evenodd" clip-rule="evenodd"`;
  const sharedPathAttrs =
    `fill="${escapeSvgAttr(baseFill)}" stroke="${id === 'bpmn-activity' ? 'none' : escapeSvgAttr(presentation.stroke)}" stroke-width="${formatSvgNumber(activityBaseStrokeWidth)}"${activityBaseDash ? ` stroke-dasharray="${activityBaseDash}"` : ''} stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke" fill-rule="evenodd" clip-rule="evenodd"`;

  const shapeMarkup =
    id === 'bpmn-event'
      ? ''
      : `<g${transformAttr}>` +
        (hasFillPaths
          ? def.fillPaths!.map((d) => `<path d="${escapeSvgAttr(d)}" ${fillOnlyAttrs} />`).join('')
          : '') +
        def.paths
          .map((d) => `<path d="${escapeSvgAttr(d)}" ${hasFillPaths ? strokeOnlyAttrs : sharedPathAttrs} />`)
          .join('') +
        `</g>`;

  const markerMarkup =
    id === 'bpmn-gateway'
      ? renderGatewayMarkerSvg(
          (args.props.gatewayType ?? 'none') as BpmnGatewayType,
          width,
          height,
          escapeSvgAttr(presentation.stroke),
          formatSvgNumber(Math.max(1.4, presentation.strokeWidth * 0.9)),
        )
      : id === 'bpmn-event'
        ? renderEventMarkerSvg(
            (args.props.eventType ?? 'start') as BpmnEventType,
            Boolean(args.props.interrupting ?? true),
            (args.props.eventMode ?? ((args.props.eventType ?? 'start') === 'end' ? 'throwing' : 'catching')) as BpmnEventMode,
            (args.props.trigger ?? 'none') as BpmnTriggerType,
            width,
            height,
            escapeSvgAttr(presentation.stroke),
          )
      : id === 'bpmn-activity'
        ? renderActivityStyleSvg(
            activityType,
            width,
            height,
            escapeSvgAttr(presentation.stroke),
            formatSvgNumber(Math.max(1.4, presentation.strokeWidth * 0.9)),
          ) +
          renderActivityMarkerSvg(
            (args.props.activityMarker ??
              (args.props.loopType === 'standard' ? 'loop' : args.props.loopType) ??
              'none') as BpmnActivityMarker,
            width,
            height,
            escapeSvgAttr(presentation.stroke),
            formatSvgNumber(Math.max(1.4, presentation.strokeWidth * 0.9)),
          ) +
          renderActivityTaskSvg(
            (args.props.taskType ?? 'none') as BpmnTaskType,
            width,
            height,
            escapeSvgAttr(presentation.stroke),
            formatSvgNumber(Math.max(1.3, presentation.strokeWidth * 0.85)),
          )
      : '';

  const labelMarkup = (() => {
    const label = presentation.label;
    if (!label) return '';

    const fontParts = resolveFontStyleParts(presentation.fontWeight, presentation.fontStyle);
    const labelFontOptions = {
      color: presentation.textColor,
      fontSize: presentation.fontSize,
      fontFamily: presentation.fontFamily,
      fontWeight: fontParts.fontWeight,
      fontStyle: fontParts.italic ? 'italic' : undefined,
      textDecoration: presentation.textDecoration,
    };

    // Match canvas behaviour in `BpmnShapeNode`:
    // - annotation renders label to the right of the bracket
    // - other BPMN shapes render label below the shape
    if (id === 'bpmn-annotation') {
      const padding = 12;
      const gap = 8;
      const armLen = Math.max(10, Math.min(18, width * 0.18));
      const x0 = Math.min(width, armLen + gap);
      const innerW = Math.max(0, width - x0 - padding);
      const textAlign = presentation.textAlign;
      const x = textAlign === 'left' ? x0 : textAlign === 'right' ? x0 + innerW : x0 + innerW / 2;
      const y = height / 2;
      return renderMultilineText(label, {
        ...labelFontOptions,
        x,
        y,
        textAnchor: textAlign === 'left' ? 'start' : textAlign === 'right' ? 'end' : 'middle',
        dominantBaseline: 'middle',
      });
    }

    const isDataStoreShape = id === 'bpmn-data-store';
    const shapeBottom = isDataStoreShape ? (66.67 * height) / 80 : height;
    const labelY = shapeBottom + BPMN_LABEL_MARGIN_Y;
    const labelW = isDataStoreShape ? Math.max(0, (75 * width) / 100) : Math.max(0, width);
    const labelH = computeBelowLabelBoxHeight(presentation.fontSize);
    const align = isDataStoreShape ? 'center' : presentation.textAlign;
    const labelX = 0;

    const boxed = renderBoxedLabel(label, labelX, 0, labelW, labelH, {
      ...labelFontOptions,
      textAlign: align,
      verticalAlign: 'top',
      padding: 0,
    });
    return boxed ? `<g transform="translate(0 ${formatSvgNumber(labelY)})">${boxed}</g>` : '';
  })();

  return shapeMarkup + markerMarkup + labelMarkup + renderTagBadgesSvg(args.props, height);
};

const createBpmnShapeManifest = (
  id: BpmnShapeId,
  dimensions: { width: number; height: number },
): ShapeManifest<BpmnShapeProps> => ({
  id,
  version: '1.0.0',
  category: 'flowchart',
  propsSchema: bpmnShapeProps,
  defaultProps: () => ({
    eventType: 'start',
    eventMode: 'catching',
    trigger: 'none',
    interrupting: true,
    gatewayType: 'none',
    activityType: 'task',
    taskType: 'none',
    loopType: 'none',
    activityMarker: 'none',
    dataType: 'data-store',
  }),
  defaultDimensions: dimensions,
  anchors: {
    snap: ['top', 'right', 'bottom', 'left', 'center'],
    perimeter: 'rect',
  },
  capabilities: {
    selectable: true,
    transformable: true,
    textEditable: true,
    connectable: true,
  },
  renderers: {
    svg: (manifestArgs) => renderBpmnShapeSVG(id, dimensions, manifestArgs),
  },
});

export const BpmnShapeManifests: ShapeManifest<BpmnShapeProps>[] = BPMN_SHAPE_IDS.map((id) => {
  const def = BPMN_SHAPES[id];
  const dims = id === 'bpmn-event' ? { width: 60, height: 60 } : { width: def.width, height: def.height };
  return createBpmnShapeManifest(id, dims);
});
